import { action, computed, type ObservableSet } from 'mobx'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import type {
  PromptDeleteDocumentFn,
  PromptUnlinkDocumentFn,
} from '@/features/documents'
import { Member } from '@/features/iam/members/Member'
import { type Id } from '@/infra/id'
import { type Document } from './Document'
import { type DocumentStore } from './DocumentStore'
import { type UploadBarViewModel } from './UploadBarViewModel'

/**
 * The upload or processing status of a listed document.
 */
export type DocumentListItemStatus =
  | { type: 'Uploading'; percent: number }
  | { type: 'Preprocessing' }
  | { type: 'AttestationScreening' }
  | { type: 'ManualAttestationRequired' }
  | { type: 'Quarantined' }
  | { type: 'Failed' }

/**
 * Options for the {@link JobDocumentListItemViewModel}.
 */
export interface JobDocumentListItemViewModelOptions {
  /**
   * The workspace whose member permissions decide what the user may do.
   */
  workspaceId: Id

  /**
   * The selected documents, shared by every item in the list.
   */
  selection: ObservableSet<Document>

  /**
   * Tracks the uploads started from the list.
   */
  uploadBarVm: UploadBarViewModel

  /**
   * The job the listed documents are linked to.
   */
  jobId: Id

  /**
   * Aborts the item's prompts, e.g. when the list deactivates.
   */
  getSignal: () => AbortSignal

  /**
   * Called when the user opens the document.
   */
  onView: (document: Document) => unknown
}

/**
 * View model for a document in a document list.
 */
export class JobDocumentListItemViewModel {
  constructor(
    readonly document: Document,
    private readonly _options: JobDocumentListItemViewModelOptions,
    private readonly _documentStore: DocumentStore,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _promptDelete: PromptDeleteDocumentFn,
    private readonly _promptUnlink: PromptUnlinkDocumentFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    this.view = this.view.bind(this)
    this.preview = this.preview.bind(this)
    this.download = this.download.bind(this)
    this.unlink = this.unlink.bind(this)
    this.delete = this.delete.bind(this)
  }

  /**
   * How much of the document's contents have been uploaded, in percent,
   * or `null` when it isn't being uploaded.
   */
  @computed
  get uploadPercent() {
    return this._options.uploadBarVm.documentUploadPercent(this.document)
  }

  /**
   * The member to show as the document's author, falling back to the owner
   * when the author is unknown.
   */
  @computed
  get author() {
    return this.document.author !== Member.none
      ? this.document.author
      : this.document.owner
  }

  /**
   * The upload or processing status to show, if any.
   */
  @computed
  get status(): DocumentListItemStatus | null {
    if (this.uploadPercent !== null) {
      return { type: 'Uploading', percent: this.uploadPercent }
    }
    switch (this.document.stage.type) {
      case 'Preprocessing':
      case 'AttestationScreening':
      case 'ManualAttestationRequired':
        return { type: this.document.stage.type }
      case 'Rejected':
        return this.document.stage.rejection === 'Quarantined'
          ? { type: 'Quarantined' }
          : { type: 'Failed' }
    }
    return null
  }

  /**
   * Whether the document is selected.
   */
  @computed
  get isSelected() {
    return this._options.selection.has(this.document)
  }

  /**
   * Whether the user may unlink the document, which only its owner can.
   */
  @computed
  get canUnlink() {
    return this.document.isOwnedBy(this._getCurrentMember().user)
  }

  /**
   * Whether the user may delete the document.
   */
  @computed
  get canDelete() {
    const { permissions } = this._getCurrentMember(this._options.workspaceId)
    return (
      permissions.docsSelfRemove ||
      permissions.docsClientRemove ||
      permissions.docsProviderRemove
    )
  }

  /**
   * Views the document.
   */
  view() {
    return this._options.onView(this.document)
  }

  /**
   * Opens the document in the browser.
   */
  preview() {
    return this._documentStore
      .download({ document: this.document, preview: true })
      .catch(this._createAlertErrorHandler('Opening the document failed'))
  }

  /**
   * Downloads the document.
   */
  download() {
    return this._documentStore
      .download({ document: this.document, preview: false })
      .catch(this._createAlertErrorHandler('Downloading failed'))
  }

  /**
   * Asks the user to confirm, then unlinks the document from the job.
   */
  unlink() {
    return this._promptUnlink({
      document: this.document,
      jobId: this._options.jobId,
      signal: this._options.getSignal(),
    })
  }

  /**
   * Asks the user to confirm, then permanently deletes the document.
   */
  delete() {
    return this._promptDelete({
      document: this.document,
      signal: this._options.getSignal(),
    })
  }

  /**
   * Toggles the document's selection.
   */
  @action.bound
  toggleSelected() {
    if (this._options.selection.has(this.document)) {
      this._options.selection.delete(this.document)
    } else {
      this._options.selection.add(this.document)
    }
  }
}
